import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { useAuth } from "@bcc/contexts/auth-context";
import { Check, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setOnboardingSuccessTestId } from "./onboarding-success.utils";

export interface OnboardingSuccessProps {
  testId?: string;
}

export function OnboardingSuccess({ testId }: OnboardingSuccessProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();
  const [platform, setPlatform] = useState<"shopify" | "woocommerce">(
    "shopify",
  );

  useEffect(() => {
    // Get the platform from localStorage
    const savedPlatform = localStorage.getItem("tempConnectedPlatform") as
      | "shopify"
      | "woocommerce";
    if (savedPlatform) {
      setPlatform(savedPlatform);
    }
  }, []);

  const handleGoToDashboard = () => {
    // Complete the onboarding
    completeOnboarding(platform);
    // Clean up temp storage
    localStorage.removeItem("tempConnectedPlatform");
    // Navigate to home
    navigate("/");
  };

  const handleReviewProducts = () => {
    completeOnboarding(platform);
    localStorage.removeItem("tempConnectedPlatform");
    navigate("/products");
  };

  return (
    <div
      data-testid={testId ?? setOnboardingSuccessTestId("container")}
      className="flex flex-col items-center gap-8"
    >
      <Card className="w-full max-w-2xl border-border-subtle shadow-sm">
        <CardContent className="flex flex-col items-center gap-6 p-12 text-center">
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-10 w-10 text-primary" />
            </div>
            <Sparkles className="absolute -right-2 -top-2 h-6 w-6 text-[#F59E0B]" />
          </div>

          <div className="flex flex-col gap-3">
            <h1
              data-testid={setOnboardingSuccessTestId("heading")}
              className="text-3xl font-semibold text-text-primary"
            >
              {t("onboarding.success.title")}
            </h1>
            <p
              data-testid={setOnboardingSuccessTestId("description")}
              className="text-base text-text-muted"
            >
              {t("onboarding.success.description")}
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid={setOnboardingSuccessTestId("dashboard-button")}
              size="lg"
              onClick={handleGoToDashboard}
            >
              {t("onboarding.success.goToDashboard")}
            </Button>
            <Button
              data-testid={setOnboardingSuccessTestId("review-products-button")}
              size="lg"
              variant="outline"
              onClick={handleReviewProducts}
            >
              {t("onboarding.success.reviewMappedProducts")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div
        data-testid={setOnboardingSuccessTestId("next-steps-section")}
        className="flex flex-col items-center gap-2 text-sm text-text-muted"
      >
        <p data-testid={setOnboardingSuccessTestId("next-steps-title")}>
          {t("onboarding.success.nextSteps.title")}
        </p>
        <ul
          data-testid={setOnboardingSuccessTestId("next-steps-list")}
          className="list-inside list-disc space-y-1 text-left"
        >
          <li data-testid={setOnboardingSuccessTestId("next-step-review")}>
            {t("onboarding.success.nextSteps.reviewMappings")}
          </li>
          <li data-testid={setOnboardingSuccessTestId("next-step-inventory")}>
            {t("onboarding.success.nextSteps.setupInventory")}
          </li>
          <li data-testid={setOnboardingSuccessTestId("next-step-orders")}>
            {t("onboarding.success.nextSteps.startOrders")}
          </li>
        </ul>
      </div>
    </div>
  );
}
